import { useState, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Filter, X, Search, ChevronDown } from 'lucide-react'
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, format as formatDate } from 'date-fns'

export interface FiltrosVencimientosType {
  tipos_impuesto?: string[]
  estados?: string[]
  responsables?: string[]
  clientes_ids?: string[]
  busqueda?: string
  desde?: string
  hasta?: string
}

type RangoPreset = 'hoy' | 'semana' | 'mes' | 'proximos30' | 'personalizado'

const ESTADOS_DISPONIBLES = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'presentado', label: 'Presentado' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'pagado', label: 'Pagado' },
  { value: 'plan_pago', label: 'Plan de Pago' },
  // ORR-178: papelera. Al elegirlo, la lista muestra los descartados (fuente aparte).
  { value: 'descartado', label: 'Descartado' },
]

// Fuera del componente: un `= []` en la firma crea un array nuevo por render y rompe la
// memoización de todo lo que dependa de la prop.
const SIN_RESPONSABLES: Array<{ id: string; nombre: string }> = []

interface FiltrosVencimientosProps {
  filtros: FiltrosVencimientosType
  onFiltrosChange: (filtros: FiltrosVencimientosType) => void
  tiposImpuesto: string[]
  /** Responsables por `usuario_id` (ORR-230 fase 4): el nombre es solo la etiqueta. */
  responsables?: Array<{ id: string; nombre: string }>
  clientes?: Array<{ id: string; nombre: string; responsablesIds?: string[] }>
}

// Multi-select combobox component
function MultiSelectCombobox({
  placeholder,
  options,
  selected,
  onToggle,
}: {
  placeholder: string
  options: Array<{ label: string; value: string }>
  selected: string[]
  onToggle: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search) return options
    const term = search.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(term))
  }, [options, search])

  const triggerLabel = selected.length === 0
    ? 'Todos'
    : selected.length === 1
      ? options.find((o) => o.value === selected[0])?.label ?? selected[0]
      : `${selected.length} seleccionados`

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 justify-between min-w-[160px] font-normal text-xs"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        {options.length > 6 && (
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={placeholder}
                className="h-7 pl-7 text-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}
        <div className="max-h-52 overflow-y-auto p-1">
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">Sin resultados</p>
          )}
          {filtered.map((option) => {
            const isActive = selected.includes(option.value)
            return (
              <div
                key={option.value}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-xs hover:bg-accent"
                onClick={() => onToggle(option.value)}
              >
                <Checkbox checked={isActive} className="h-3.5 w-3.5" tabIndex={-1} />
                <span className="truncate">{option.label}</span>
              </div>
            )
          })}
        </div>
        {selected.length > 0 && (
          <div className="border-t p-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs w-full"
              onClick={() => {
                selected.forEach((v) => onToggle(v))
              }}
            >
              Limpiar seleccion
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export function FiltrosVencimientos({
  filtros,
  onFiltrosChange,
  tiposImpuesto,
  responsables = SIN_RESPONSABLES,
  clientes = [],
}: FiltrosVencimientosProps) {
  // Default 'mes' coincide con el filtro inicial seteado en Calendario.tsx (mes actual)
  const [rangoActivo, setRangoActivo] = useState<RangoPreset>('mes')

  // Toggle a value in an array filter
  const toggleArrayFilter = (campo: 'tipos_impuesto' | 'estados' | 'responsables' | 'clientes_ids', valor: string) => {
    const current = filtros[campo] || []
    const next = current.includes(valor)
      ? current.filter((v) => v !== valor)
      : [...current, valor]
    onFiltrosChange({
      ...filtros,
      [campo]: next.length > 0 ? next : undefined,
    })
  }

  // Remove a single value from an array filter
  const removeFromArrayFilter = (campo: 'tipos_impuesto' | 'estados' | 'responsables' | 'clientes_ids', valor: string) => {
    const current = filtros[campo] || []
    const next = current.filter((v) => v !== valor)
    onFiltrosChange({
      ...filtros,
      [campo]: next.length > 0 ? next : undefined,
    })
  }

  const aplicarRango = (rango: RangoPreset) => {
    setRangoActivo(rango)
    const hoy = new Date()

    let desde: string | undefined
    let hasta: string | undefined

    switch (rango) {
      case 'hoy':
        desde = formatDate(startOfDay(hoy), 'yyyy-MM-dd')
        hasta = formatDate(endOfDay(hoy), 'yyyy-MM-dd')
        break
      case 'semana':
        desde = formatDate(startOfWeek(hoy, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        hasta = formatDate(endOfWeek(hoy, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        break
      case 'mes':
        desde = formatDate(startOfMonth(hoy), 'yyyy-MM-dd')
        hasta = formatDate(endOfMonth(hoy), 'yyyy-MM-dd')
        break
      case 'proximos30':
        desde = formatDate(hoy, 'yyyy-MM-dd')
        hasta = formatDate(addDays(hoy, 30), 'yyyy-MM-dd')
        break
      case 'personalizado':
        return
    }

    onFiltrosChange({
      ...filtros,
      desde,
      hasta,
    })
  }

  const limpiarFiltros = () => {
    onFiltrosChange({})
    setRangoActivo('personalizado')
  }

  // Deduplicated clients (by id AND nombre to avoid visual duplicates)
  const clientesUnicos = useMemo(() => {
    const seenById = new Map<string, typeof clientes[number]>()
    const seenByNombre = new Set<string>()
    for (const c of clientes) {
      if (!seenById.has(c.id) && !seenByNombre.has(c.nombre.toLowerCase())) {
        seenById.set(c.id, c)
        seenByNombre.add(c.nombre.toLowerCase())
      }
    }
    return Array.from(seenById.values())
  }, [clientes])

  // Clients filtered by selected responsables
  const clientesFiltrados = useMemo(() => {
    if (!filtros.responsables?.length) return clientesUnicos
    const elegidos = new Set(filtros.responsables)
    return clientesUnicos.filter((c) => c.responsablesIds?.some((id) => elegidos.has(id)))
  }, [clientesUnicos, filtros.responsables])

  // Combobox options
  const impuestoOptions = useMemo(
    () => tiposImpuesto.map((t) => ({ label: t, value: t })),
    [tiposImpuesto],
  )

  const clienteOptions = useMemo(
    () => clientesFiltrados.map((c) => ({ label: c.nombre, value: c.id })),
    [clientesFiltrados],
  )

  const hayFiltrosActivos = useMemo(() => {
    return !!(
      filtros.tipos_impuesto?.length ||
      filtros.estados?.length ||
      filtros.responsables?.length ||
      filtros.clientes_ids?.length ||
      filtros.busqueda ||
      filtros.desde ||
      filtros.hasta
    )
  }, [filtros])

  // Active filter chips
  const filtrosActivos = useMemo(() => {
    const activos: Array<{ key: string; campo: 'tipos_impuesto' | 'estados' | 'responsables' | 'clientes_ids' | 'desde' | 'hasta'; valor?: string; label: string }> = []

    filtros.estados?.forEach((estado) => {
      const label = ESTADOS_DISPONIBLES.find((e) => e.value === estado)?.label || estado
      activos.push({ key: `estado-${estado}`, campo: 'estados', valor: estado, label })
    })

    filtros.tipos_impuesto?.forEach((tipo) => {
      activos.push({ key: `impuesto-${tipo}`, campo: 'tipos_impuesto', valor: tipo, label: tipo })
    })

    filtros.responsables?.forEach((usuarioId) => {
      const responsable = responsables.find((r) => r.id === usuarioId)
      activos.push({
        key: `resp-${usuarioId}`,
        campo: 'responsables',
        valor: usuarioId,
        label: responsable?.nombre ?? 'Responsable',
      })
    })

    filtros.clientes_ids?.forEach((clienteId) => {
      const cliente = clientesUnicos.find((c) => c.id === clienteId)
      if (cliente) {
        activos.push({ key: `cliente-${clienteId}`, campo: 'clientes_ids', valor: clienteId, label: cliente.nombre })
      }
    })

    if (filtros.desde && filtros.hasta) {
      activos.push({ key: 'periodo', campo: 'desde', label: `${filtros.desde} → ${filtros.hasta}` })
    } else if (filtros.desde) {
      activos.push({ key: 'desde', campo: 'desde', label: `Desde: ${filtros.desde}` })
    } else if (filtros.hasta) {
      activos.push({ key: 'hasta', campo: 'hasta', label: `Hasta: ${filtros.hasta}` })
    }

    return activos
  }, [filtros, clientesUnicos, responsables])

  const handleRemoveFiltroActivo = (filtro: typeof filtrosActivos[number]) => {
    if (filtro.campo === 'estados' || filtro.campo === 'tipos_impuesto' || filtro.campo === 'responsables' || filtro.campo === 'clientes_ids') {
      removeFromArrayFilter(filtro.campo, filtro.valor!)
    } else if (filtro.campo === 'desde') {
      onFiltrosChange({ ...filtros, desde: undefined, hasta: undefined })
      setRangoActivo('personalizado')
    } else if (filtro.campo === 'hasta') {
      onFiltrosChange({ ...filtros, hasta: undefined })
      setRangoActivo('personalizado')
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {/* Header + limpiar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Filter className="h-4 w-4" />
            Filtros
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={limpiarFiltros}
            className={`text-muted-foreground h-7 transition-opacity duration-200 ${
              hayFiltrosActivos ? 'opacity-100 text-destructive hover:text-destructive' : 'opacity-0 pointer-events-none'
            }`}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Limpiar filtros
          </Button>
        </div>

        {/* Row 1: Estado + Responsable + Impuesto + Cliente */}
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Estado</Label>
            <MultiSelectCombobox
              placeholder="Buscar estado..."
              options={ESTADOS_DISPONIBLES.map((e) => ({ value: e.value, label: e.label }))}
              selected={filtros.estados || []}
              onToggle={(v) => toggleArrayFilter('estados', v)}
            />
          </div>

          {responsables.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Responsable</Label>
              <MultiSelectCombobox
                placeholder="Buscar responsable..."
                options={responsables.map((r) => ({ value: r.id, label: r.nombre }))}
                selected={filtros.responsables || []}
                onToggle={(v) => toggleArrayFilter('responsables', v)}
              />
            </div>
          )}

          {tiposImpuesto.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Impuesto</Label>
              <MultiSelectCombobox
                placeholder="Buscar impuesto..."
                options={impuestoOptions}
                selected={filtros.tipos_impuesto || []}
                onToggle={(v) => toggleArrayFilter('tipos_impuesto', v)}
              />
            </div>
          )}

          {clientesUnicos.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <MultiSelectCombobox
                placeholder="Buscar cliente..."
                options={clienteOptions}
                selected={filtros.clientes_ids || []}
                onToggle={(v) => toggleArrayFilter('clientes_ids', v)}
              />
            </div>
          )}
        </div>

        {/* Row 3: Período */}
        <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Periodo</Label>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { key: 'hoy' as const, label: 'Hoy' },
                { key: 'semana' as const, label: 'Sem' },
                { key: 'mes' as const, label: 'Mes' },
                { key: 'proximos30' as const, label: '30d' },
              ].map((rango) => (
                <Button
                  key={rango.key}
                  variant={rangoActivo === rango.key ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 text-xs px-2.5"
                  onClick={() => aplicarRango(rango.key)}
                >
                  {rango.label}
                </Button>
              ))}
              <Input
                type="date"
                className="h-8 w-[150px] text-xs"
                value={filtros.desde || ''}
                onChange={(e) => {
                  const desde = e.target.value || undefined
                  // Un "hasta" anterior al "desde" invierte el rango, y con el rango invertido
                  // el filtro cronológico de Calendario.tsx descarta todas las filas.
                  const hasta = desde && filtros.hasta && filtros.hasta < desde ? undefined : filtros.hasta
                  onFiltrosChange({ ...filtros, desde, hasta })
                  setRangoActivo('personalizado')
                }}
              />
              <span className="text-xs text-muted-foreground">—</span>
              <Input
                type="date"
                className="h-8 w-[150px] text-xs"
                value={filtros.hasta || ''}
                onChange={(e) => {
                  const hasta = e.target.value || undefined
                  // Simétrico: un "desde" posterior al "hasta" invierte el mismo rango.
                  const desde = hasta && filtros.desde && filtros.desde > hasta ? undefined : filtros.desde
                  onFiltrosChange({ ...filtros, desde, hasta })
                  setRangoActivo('personalizado')
                }}
              />
            </div>
          </div>
        </div>

        {/* Active filter chips - reserved height to avoid layout shift */}
        <div className="flex flex-wrap items-center gap-1.5 min-h-[24px]">
          {filtrosActivos.map((filtro) => (
            <Badge
              key={filtro.key}
              variant="secondary"
              className="gap-1 pr-1 h-5 text-xs"
            >
              {filtro.label}
              <button
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                onClick={() => handleRemoveFiltroActivo(filtro)}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </Badge>
          ))}
          {filtrosActivos.length === 0 && (
            <span className="text-xs text-muted-foreground/50">Sin filtros activos</span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
